import { useEffect, useState } from 'react';

// Reactive media-query hook. SSR-safe (defaults to false when window is
// missing). Defensive against jsdom's partial matchMedia stubs that can
// return `undefined` for the MediaQueryList — accessing `.matches` on
// that would throw, taking the entire React tree down.
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query)?.matches ?? false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(query);
    if (!mql) return;
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Deprecated fallback — Safari < 14 (kept for parity with the old
    // useIsDesktop hook deleted in Sprint UI-1).
    type LegacyMql = { addListener(fn: (e: MediaQueryListEvent) => void): void; removeListener(fn: (e: MediaQueryListEvent) => void): void };
    (mql as unknown as LegacyMql).addListener(handler);
    return () => (mql as unknown as LegacyMql).removeListener(handler);
  }, [query]);

  return matches;
}

// Tailwind's md breakpoint = 768px.
export const MD_QUERY = '(min-width: 768px)';
